import React, { PureComponent } from 'react';
import { AccordionItem } from '@nextiva/next-ui';
import localStore from 'store2';
import cx from 'classnames';
import { omit } from 'lodash';

import { getCustomReport } from '../../dataProvider';
import {
  NoData,
  DateRanges as DateRangesJs,
  ErrorBoundary,
  DefaultSpinner,
  ErrorPlaceholder,
} from 'components';
import AppData from 'app_data';

import ChartJs from '../Chart';
import Dropzone from 'components/dropzone';

import Styles from './CustomReport.module.scss';
import IntervalRunner from 'analytics-ui/IntervalRunner';
import { useScopedLocationIds } from 'hooks/useAdminGroup/useScopedLocationIds';
import { applyLocationScope } from 'hooks/useAdminGroup/applyLocationScope';
import { addScopeLocationsFilter } from 'hooks/useAdminGroup/addScopeLocationsFilter';
import LocationScopeGate from 'hooks/useAdminGroup/LocationScopeGate';
import type { ChartFilters } from 'hooks/useAdminGroup/applyLocationScope';

const onSelectDropzone = () => {};

/**
 * `components`' DateRanges and `../Chart` are untyped JS whose default exports
 * are memo()-wrapped, so TS infers a zero-prop component and rejects every
 * attribute. Widen at the import boundary and keep the render body as-is,
 * rather than threading `any` through it.
 */
type UntypedComponent = React.ComponentType<Record<string, unknown>>;
const DateRanges = DateRangesJs as UntypedComponent;
const Chart = ChartJs as UntypedComponent;

/** One entry in the report's date-range list. */
export interface ReportTimeRange {
  start?: string;
  end?: string;
  [key: string]: unknown;
}

/**
 * The body posted to /v2/nextos/reports/custom/run. `id` selects a saved
 * report; without one the definition comes from localStorage, so the rest of
 * the shape is whatever the builder stored.
 */
export interface CustomReportRequest {
  id?: string;
  filters?: ChartFilters;
  timeRanges?: ReportTimeRange[];
  [key: string]: unknown;
}

/**
 * The normalized /custom/run response. For a SAVED report this is the STORED
 * definition with results merged in, which is why `filters` needs re-scoping
 * in getReport().
 */
export interface CustomReportResponse {
  format?: string;
  display?: string;
  title?: string;
  timezone?: string;
  timeRanges?: ReportTimeRange[];
  filterLabels?: unknown;
  filters?: ChartFilters;
  data?: unknown;
  isCancel?: boolean;
  errors?: unknown;
  [key: string]: unknown;
}

/** The narrower shape formatCallLogData reads off an external report. */
interface ExternalCallLogResponse {
  data: unknown[][];
}

export interface CustomReportProps {
  /** Router match; `params.id` selects a saved report. */
  match: { params: { id?: string } };
  /** The /custom/run caller. Injectable so specs can supply their own. */
  getReport: (report: CustomReportRequest) => Promise<CustomReportResponse>;
  /** True on the public external-reports route. */
  isExternal: boolean;
  /** Breadcrumb scope applies: LBA on and a non-root selection. */
  scopeApplyScope: boolean;
  /** externalIds of everything in the active breadcrumb scope. */
  scopeExternalIds: Set<string>;
  /** Disables the date picker and hides the real-time icon. */
  isDisabledData?: boolean;
}

/** What the wrapper passes through; it supplies the two scope props itself. */
type CustomReportOwnProps = Omit<
  CustomReportProps,
  'scopeApplyScope' | 'scopeExternalIds'
>;

interface CustomReportState {
  isLoading: boolean;
  isOpenFilters: boolean;
  isError?: boolean;
  realTime?: boolean;
  data?: CustomReportResponse;
}


class CustomReport extends PureComponent<CustomReportProps, CustomReportState> {
  static defaultProps = {
    getReport: getCustomReport,
    isExternal: false,
  };

  state: CustomReportState = {
    isLoading: true,
    isOpenFilters: false,
  };

  componentDidMount() {
    // Check for URL params, otherwise run from local storage
    this.getReport();
  }

  // The breadcrumb scope arrives as props and getReport() reads it, but
  // nothing else re-ran the fetch when it changed: LocationScopeGate only
  // unmounts this component while the scope is LOADING, which is never true
  // for a LOCATION selection and stops being true for an ADMIN_GROUP as soon
  // as the hierarchy is in the react-query cache. Without this, changing the
  // breadcrumb left BOTH the chart and the call-log table showing the
  // mount-time scope until a reload or a route change (real-time mode masked
  // it, because IntervalRunner re-invokes getReport on every tick).
  // Compare ONLY the scope props: scopeExternalIds is memoized upstream in
  // useScopedLocationIds so reference equality is safe here, and diffing
  // anything getReport() itself writes to state would loop.
  componentDidUpdate(prevProps: CustomReportProps) {
    if (
      prevProps.scopeApplyScope !== this.props.scopeApplyScope ||
      prevProps.scopeExternalIds !== this.props.scopeExternalIds
    ) {
      this.getReport();
    }
  }

  // the backend sends call log data in a different format for custom reports
  // than it does for the call-log endpoint.  Fix it here until the backend
  // can be fixed.
  formatCallLogData(response: ExternalCallLogResponse) {
    return {
      "tables": [
        {
          "rows": response.data[0],
          "headers": [
            {
              "id": "direction_icon"
            },
            {
              "id": "user_id",
              "label": "Name"
            },
            {
              "id": "start_time",
              "label": "Time of Call",
            },
            {
              "id": "talk_duration_seconds",
              "label": "Duration",
              "duration": true,
            },
            {
              "id": "direction",
              "label": "Direction",
            },
            {
              "id": "connected",
              "label": "Answered",
            },
            {
              "id": "customer_area_code_state",
              "label": "State",
            },
            {
              "id": "calling_number",
              "label": "From",
            },
            {
              "id": "called_number",
              "label": "To",
            }
          ],
          "sort": {
            "header": "start_time"
          }
        }
      ],
      "limit": response.data[0].length,
      "total_count": response.data[0].length,
      "offset": 0
    };
  }

  getReport = async (): Promise<void> => {
    const { getReport, match, isExternal, scopeApplyScope, scopeExternalIds } = this.props;
    const { id } = match.params;
    const { realTime, data } = this.state;
    const { timeRanges, format } = data || ({} as CustomReportResponse);

    const report: CustomReportRequest = Object.assign(
      { id },
      !id && localStore.get('customReport'),
      timeRanges && { timeRanges },
    );

    if (timeRanges) {
      report.timeRanges = realTime
        ? timeRanges.map((timeRange) => omit(timeRange, 'end'))
        : timeRanges;
    }

    // Floor filter: the Custom Report builder lets a user manually pick any
    // filter dimension (including locations/groups), so the admin-group
    // scope must be forced in here too, not just at the ReportPageChart
    // pinch point -- otherwise a scoped user could still see out-of-scope
    // data via a hand-built custom report.
    report.filters = applyLocationScope(report.filters, scopeApplyScope, scopeExternalIds);

    this.setState({
      isError: false,
      isLoading: true,
    });

    const response = await getReport(report);

    if (response.isCancel) {
      return;
    }

    if (response.errors) {
      this.setState({
        isError: true,
        isLoading: false,
      });
      return;
    }

    /**
      If the report is external we fix up the call log data that comes from
      the public endpoint for use, otherwise unset is so the component will
      call the call-log endpoint.
    */
    if(response.format === 'call_log') {
        response.data = isExternal
          ? this.formatCallLogData(response as unknown as ExternalCallLogResponse)
          : undefined;
    }

    // For a SAVED report the response is the STORED definition -- the backend
    // discards the client body whenever the request carries an id -- so the
    // `filters` echoed back here are the saved, UNSCOPED ones, not the scoped
    // ones sent above. Chart.js hands them straight to ReportCallLogContainer,
    // which POSTs them to /v2/nextos/reports/call-log for both the table and
    // the CSV email export. Without re-scoping them those two fetches run
    // account-wide for any role whose server-side clamp is unrestricted
    // (enterprise_admin, multi_location_admin). Gating on format also keeps an
    // empty `{}` response empty so render()'s NoData branch still matches.
    //
    // call_log ONLY, deliberately. Chart.js routes sms_log to
    // /v2/nextos/reports/sms-log, and SMSLogTableResourceV2 builds
    // MessageEventsQuery by hand rather than from base_params: it never passes
    // `locations`, so it consults neither this key nor the role clamp, and
    // message_events carries no location column to filter on (PRDDEV-61172).
    // Sending scope_locations there would be inert AND would falsely signal a
    // scoped SMS table. The SMS gap is tracked in PRDDEV-61866; add sms_log
    // back here once that endpoint can honour the key.
    //
    // Scoped via `scope_locations`, NOT a filters.locations merge -- see
    // addScopeLocationsFilter for why a saved definition must not gain a
    // `locations` key it did not already have.
    if (response.format === 'call_log') {
      response.filters = addScopeLocationsFilter(
        response.filters,
        scopeApplyScope,
        scopeExternalIds,
      );
    }

    this.setState({
      data: response,
      isLoading: false,
    });
  };


  onRealTimeChange = (realTime: boolean) => {
    this.setState({
      realTime,
    });
  };


  onDateChange = (timeRanges: ReportTimeRange[]) => {
    const { data } = this.state;
    this.setState({
      data: {
        ...data,
        timeRanges,
      },
    }, this.getReport);
  };


  onToggleFilters = () => {
    const { isOpenFilters } = this.state;

    this.setState({
      isOpenFilters: !isOpenFilters,
    });
  }


  render() {
    const {
      data,
      realTime,
      isError,
      isLoading,
      isOpenFilters,
    } = this.state;

    const { isDisabledData } = this.props;

    if (!data) {
      return isLoading ? (
        <DefaultSpinner />
      ) : (
        <ErrorPlaceholder isAbsolutePosition />
      )
    } else if (Object.keys(data).length === 0) {
      return (
        <NoData isAbsolutePosition />
      );
    }

    const {
      title,
      timezone,
      timeRanges,
      filterLabels,
    } = data;

    return (
      <div className={Styles.report}>
        <ErrorBoundary>
          {title && (
            <h2 data-testid="custom-report-title" className={Styles.title}>{title}</h2>
          )}

          {timeRanges && (
            <DateRanges
              isRealTime={realTime}
              timeRanges={timeRanges}
              timezone={timezone || AppData.user.timezone}
              onChange={this.onDateChange}
              onRealTimeChange={this.onRealTimeChange}
              disabled={isDisabledData}
              withRealTimeIcon={!isDisabledData}
              theme={{ dateRange: Styles.dateRange }}
            />
          )}

          <IntervalRunner isActive={realTime} action={this.getReport}>
            <div className={cx(Styles.content, { [Styles.chartLoading]: isLoading })}>
              {isLoading && (
                <DefaultSpinner />
              )}

              {isError ? (
                <ErrorPlaceholder />
              ) : (
                <Chart {...data} realTime={realTime} />
              )}
            </div>

            <AccordionItem
              hideBar
              label="Details"
              isOpen={isOpenFilters}
              className={Styles.filters}
              onItemToggleRequest={this.onToggleFilters}
            >
              <Dropzone
                onSelect={onSelectDropzone} // for showing labels without changing
                items={filterLabels}
                perPage={24}
                static
              />
            </AccordionItem>
          </IntervalRunner>
        </ErrorBoundary>
      </div>
    );
  }
}


const CustomReportWithScope = (props: Record<string, unknown>) => {
  const scope = useScopedLocationIds();

  return (
    <LocationScopeGate scope={scope}>
      <CustomReport
        {...(props as unknown as CustomReportOwnProps)}
        scopeApplyScope={scope.applyScope}
        scopeExternalIds={scope.scopeExternalIds}
      />
    </LocationScopeGate>
  );
};

export default CustomReportWithScope;
